import { Avatar, StatusComponent } from '@efexteam/components';
import { Actions, Can, ROLE_IDS, Subjects } from '@efexteam/permission-manager';
import KeyboardArrowRightOutlinedIcon from '@mui/icons-material/KeyboardArrowRightOutlined';
import {
  IconButton,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { IMembers } from '@pages/profile/types';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import MembersTableStyles from './MembersTable.style';
import { IMembersTable } from './types/MembersTable.type';

const MembersTable = ({ members, loading }: IMembersTable) => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const editMember = (member: IMembers) => {
    navigate(`/edit-team-member/${member.id}`);
  };

  const renderHeadCell = (label: string) => (
    <TableCell>
      <Typography variant="caption" className={MembersTableStyles.headCell}>
        {label}
      </Typography>
    </TableCell>
  );

  const renderBodyCell = (label: string | React.ReactNode) => (
    <TableCell>{label}</TableCell>
  );

  const renderSkeleton = () =>
    Array.from({ length: 4 }).map((_, index) => (
      // eslint-disable-next-line react/no-array-index-key
      <TableRow key={index}>
        <TableCell colSpan={4}>
          <Skeleton variant="rectangular" width="100%" height={20} />
        </TableCell>
      </TableRow>
    ));

  return (
    <TableContainer component={Paper} elevation={0} className="!mt-4">
      <Table
        aria-label="members table"
        className={clsx(MembersTableStyles.table, 'mui-table')}
      >
        <TableHead className={MembersTableStyles.head}>
          <TableRow>
            {renderHeadCell(t('teamTableName'))}
            {renderHeadCell(t('teamTableRole'))}
            {renderHeadCell(t('teamTableStatus'))}
            {renderHeadCell('')}
          </TableRow>
        </TableHead>
        <TableBody>
          {loading && renderSkeleton()}
          {!loading &&
            members.map((member) => (
              <TableRow key={member.id}>
                {renderBodyCell(
                  <Avatar
                    showLabel
                    username={`${member.naturalPerson.firstName} ${member.naturalPerson.lastName}`}
                    size="sm"
                  />
                )}
                {renderBodyCell(
                  <Typography variant="body2">
                    {t(
                      `member${ROLE_IDS[
                        member.platformRole
                          ?.id as unknown as keyof typeof ROLE_IDS
                      ]?.toUpperCase()}`
                    )}
                  </Typography>
                )}
                {renderBodyCell(
                  member.platformRole?.name.toUpperCase() !== 'OWNER' && (
                    <StatusComponent
                      displayVariant="chip"
                      size="small"
                      status={member.isActive ? 'positive' : 'negative'}
                      label={t(
                        `member${member.isActive ? 'Active' : 'Inactive'}`
                      )}
                      className={MembersTableStyles.statusChip}
                      disabled={!member.isActive}
                    />
                  )
                )}
                <TableCell className="!text-right">
                  <Can action={Actions.UPDATE} subject={Subjects.TEAM_MEMBERS}>
                    <IconButton
                      size="small"
                      onClick={() => editMember(member)}
                      disabled={!member.isActive}
                    >
                      <KeyboardArrowRightOutlinedIcon />
                    </IconButton>
                  </Can>
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default MembersTable;
